//
import Utilities from "./utilities";
import QueryBuilder from "./query-builder";

const veevaUtil = new Utilities(window);
let queryBuilder = null;

/** Veeva MyInsights Data Access Library */
class DataAccessLibrary {
  constructor(global) {
    this._version = "my-insights-library-version"; // this value is replaced in the build process with the actual value.
    this._queries = {
      translations: {
        object: "Message_vod__c",
        fields: ["Name", "Category_vod__c", "Language_vod__c", "Text_vod__c"],
        where: ""
      }
    };
    this._dateRE = /date/i;
    this._urlRE = /_url_/i;
    this._translationsCache = {};
    this._ISO_DATE_REGEX = /[0-9]{4}-[0-9]{2}-[0-9]{2}(T|\s)[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{1,3}Z/;
    this._VALID_DATE_FORMAT_REGEX = /[0-9]{4}.[0-9]{2}.[0-9]{2}(.[0-9]{2})?(.[0-9]{2})?(.[0-9]{2})?(.[0-9]{1,3})?[zZ]?/;
    this._messageRegistry = {};
    this.global = global;

    try {
      queryBuilder = new QueryBuilder(window);
    } catch (ex) {
      //NOOP - if queryBuilder does not exist, do not cause base dataService to fail
    }

    this._addMessageListener(this._messageListener.bind(this));
  }
  /**
   * @param {Function}
   * @return {undefined}
   * @private
   */
  _addMessageListener(callback) {
    this.global.addEventListener(
      "myinsights-response-message",
      callback,
      false
    );
  }
  /**
   * @param  {Object}
   * @return {undefined}
   * @private
   */
  _messageListener(event) {
    const { messageId, success, data } = event.detail;
    if (this._messageRegistry[messageId]) {
      const promise = this._messageRegistry[messageId];
      if (success) {
        promise.resolve(data);
      } else {
        promise.reject(data);
      }
      delete this._messageRegistry[messageId];
    } else {
      throw new Error("Promise not found in registry");
    }
  }

  _isISODateFormat(date) {
    return this._ISO_DATE_REGEX.test(date);
  }

  _isValidDateFormat(date) {
    return this._VALID_DATE_FORMAT_REGEX.test(date);
  }

  _normalizeTimeDigits(timeDigits) {
    return timeDigits ? parseInt(timeDigits, 10) : 0;
  }

  _parseDate(date) {
    if (this._isISODateFormat(date)) {
      return new Date(date);
    }
    const dateParts = date.match(/(\d+)/g);
    let realDate = null;
    if (dateParts) {
      const extractedDigits = {
        year: this._normalizeTimeDigits(dateParts[0]),
        month: this._normalizeTimeDigits(dateParts[1]) - 1,
        date: this._normalizeTimeDigits(dateParts[2]),
        hours: this._normalizeTimeDigits(dateParts[3]),
        minutes: this._normalizeTimeDigits(dateParts[4]),
        seconds: this._normalizeTimeDigits(dateParts[5]),
        ms: this._normalizeTimeDigits(dateParts[6])
      };
      realDate = new Date(
        extractedDigits.year,
        extractedDigits.month,
        extractedDigits.date,
        extractedDigits.hours,
        extractedDigits.minutes,
        extractedDigits.seconds,
        extractedDigits.ms
      );
    }
    return realDate;
  }

  _getCRMDate(date) {
    let newDate;

    if (this._isISODateFormat(date) || this._isValidDateFormat(date)) {
      newDate = this._parseDate(date);
    }

    return newDate;
  }

  _getResultSet(results) {
    const keys = Object.keys(results);
    let k;
    let result = { data: {} };
    const disallowedKeys = {
      success: 1,
      record_count: 1,
      fieldLabels: 1,
      object: 1,
      command: 1,
      deferredId: 1
    };
    function getLabelFromLabels(name, labels) {
      let l = labels.length;
      let label;
      while (l--) {
        if (labels[l].name === name) {
          label = labels[l].display;
          break;
        }
      }
      return label;
    }
    while ((k = keys.pop())) {
      if (!disallowedKeys[k]) {
        const { object } = results;
        result = {
          data: results[k],
          object,
          name: k,
          fieldLabels: results.fieldLabels
        };
        const { data } = result;
        const urlTrimProtocolRE = /^(http|https):\/\//i;
        const urlTrimDubRE = /www\./i;
        const { fieldLabels } = results;

        result.object.name = k;

        for (let i = data.length; i--; ) {
          const dataKeys = Object.keys(data[i]);
          let d;
          while ((d = dataKeys.pop())) {
            // d is the key of the data point
            if (this._dateRE.test(d)) {
              // this is a date field
              const tempDate = data[i][d];
              const realDate = this._getCRMDate(tempDate);
              if (realDate) {
                data[i].date = data[i][d] = {
                  value: realDate,
                  display: [
                    realDate.getFullYear(),
                    realDate.getMonth() + 1,
                    realDate.getDate()
                  ].join("-"),
                  dataType: "date"
                };
              } else {
                data[i].date = data[i][d] = {
                  value: tempDate,
                  display: tempDate,
                  dataType: "date"
                };
              }
            } else if (this._urlRE.test(d)) {
              const tempUrl = data[i][d];
              data[i][d] = {
                value: tempUrl,
                display:
                  tempUrl && tempUrl.length
                    ? `<a href="${tempUrl}" target="_blank">${tempUrl
                        .replace(urlTrimProtocolRE, "")
                        .replace(urlTrimDubRE, "")
                        .slice(0, 12)}\u2026</a>`
                    : null,
                dataType: "url"
              };
            } else if (data[i][d] && data[i][d].value) {
              const formattedData = {
                value: data[i][d].value,
                display: data[i][d].display,
                dataType: "string"
              };

              data[i][d] = formattedData;
            } else {
              data[i][d] = {
                value: data[i][d],
                display: data[i][d],
                dataType: "string"
              };
            }
            data[i][d].label = getLabelFromLabels(d, fieldLabels);
          }
        }
      }
    }
    return result;
  }

  _runQueryHelper(queryConfig) {
    const { config } = queryConfig;
    const picklistsAvailable = config.picklists && config.picklists.length;

    const reportQueryError = e => {
      throw e;
    };

    const getPicklists = resp => {
      let thisPicklist = config.picklists.pop();

      const nextPicklist = () => {
        return this.getPicklistValueLabels(config.object, thisPicklist).then(
          picklistResp => {
            const thisPicklistsResp = picklistResp[config.object];
            // got the object containing picklist values for each picklist field
            const picklistKeys = Object.keys(thisPicklistsResp);
            let pk;
            while ((pk = picklistKeys.pop())) {
              // loop over each of the piclist value lists
              const picklistName = pk; // the picklist name is also the name of the field in the record
              const picklistValues = thisPicklistsResp[pk]; // this object has all the possible values for the picklist
              for (
                let records = resp[config.object], r = records.length - 1;
                r--;

              ) {
                const record = records[r];
                if (
                  record[picklistName] &&
                  (picklistValues[record[picklistName].value] ||
                    picklistValues[record[picklistName]])
                ) {
                  if (record[picklistName].value) {
                    record[picklistName].display =
                      picklistValues[record[picklistName].value];
                  } else if (picklistValues[record[picklistName]]) {
                    const formattedRecord = {
                      value: record[picklistName],
                      display: picklistValues[record[picklistName]]
                    };
                    record[picklistName] = formattedRecord;
                  }
                }
              }
            }
          },
          e => {
            console.warn("picklist error", e);
          }
        );
      };
      const findPicklists = function findPicklists() {
        return nextPicklist().then(() => {
          thisPicklist = config.picklists.pop();
          let next;
          if (thisPicklist) {
            next = findPicklists();
          }
          return next;
        });
      };
      return findPicklists();
      // TODO: Make this serial or recursive in some way or modify the queue to be lower level.
      /* return nextPicklist().then(() => {
        thisPicklist = config.picklists.pop();
        if (thisPicklist) {
          return nextPicklist().then(() => {
            thisPicklist = config.picklists.pop();
            if (thisPicklist) {
              return nextPicklist();
            }
          });
        }
      }); */
    };

    // Get object label
    const objectLabelDeferred = this.getObjectLabels([config.object]);

    // Get field label
    const fieldLabelDeferred = this.getFieldLabels(config);

    // Query record
    const queryRecordDeferred = this.queryRecord(config);

    // When everything is done
    const allPromises = [
      objectLabelDeferred,
      fieldLabelDeferred,
      queryRecordDeferred
    ];
    return Promise.all(allPromises).then(allResp => {
      const [objectLabelResp, fieldLabelResp, queryRecordResp] = allResp;

      const runQueryResp = veevaUtil.copyObject(queryRecordResp);
      runQueryResp.object = objectLabelResp[config.object];
      runQueryResp.fieldLabels = fieldLabelResp;

      if (picklistsAvailable) {
        return getPicklists(runQueryResp).then(function returnResultSet() {
          return this._getResultSet(runQueryResp);
        }, reportQueryError);
      }
      return this._getResultSet(runQueryResp);
    }, reportQueryError);
  }

  _getMessageId() {
    const generateId = function generateId() {
      return Math.random()
        .toString(36)
        .substr(2, 9);
    };
    let messageId = generateId();
    while (this._messageRegistry[messageId]) {
      messageId = generateId();
    }
    return messageId;
  }
  /**
   * Get a request object formatted for passing through the platform app API
   * @param  {string} command The name of the command you wish to execute in the platform app API
   * @param  {Object} data    Contains object, fields, where, sort, limit
   * @return {Object}         The request object ready for passing to the platform API
   */
  _constructRequest(command, data) {
    return { command, messageId: this._getMessageId(), ...data };
  }
  /**
   * Validate that a query has the correct shape and types
   * @param {Object} queryConfig The config describing the query
   * @return {boolean} Boolean indicating if the query config is of a valid format.
   */
  _validateQueryConfig(queryConfig) {
    const valid = true;
    const errors = [];
    /* object required string */
    /* fields: required array */
    /* where: optional string */
    /* sort: optional array */
    /* limit: optional number */
    if (queryConfig) {
      if (queryConfig instanceof Object) {
        const { object, fields, where, sort, limit } = queryConfig;
        if (object) {
          if (typeof object !== "string") {
            errors.push(
              '\nMember "object" of argument queryConfig must be a string.'
            );
          }
          if (!fields || !(fields instanceof Array) || !fields.length) {
            errors.push(
              '\nMember "fields" of argument queryConfig must be an Array of object names.'
            );
          }
          if (typeof where === "string" && !where) {
            errors.push(
              '\nMember "where" of argument queryConfig must be a string.'
            );
          }
          if (
            sort &&
            (!Array.isArray(sort) ||
              sort.some(statement => typeof statement !== "string"))
          ) {
            errors.push(
              '\nMember "sort" of argument queryConfig must be an Array of strings.'
            );
          }
          if (limit && typeof limit !== "number") {
            errors.push(
              '\nMember "object" of argument queryConfig must be a non-zero number.'
            );
          }
        } else {
          errors.push('\nMember "object" of argument queryConfig is required.');
        }
      } else {
        errors.push("\nArgument queryConfig is not of type Object");
      }
    } else {
      errors.push("\nArgument queryConfig is required");
    }
    if (errors.length) {
      throw new Error(errors);
    }
    return valid;
  }
  /**
   * Lowest level method for passing request to the platform app
   * @param  {Object} request   The request object to be passed as the message payload to the platform app
   * @return {Promise}          The promise that will resolve with the response.
   */
  _query(request) {
    return new Promise((resolve, reject) => {
      this._messageRegistry[request.messageId] = { resolve, reject };
      // if (veevaUtil.isWindowsMobile()) {
      //   this.global.external.notify(request);
      // } else if (veevaUtil.isIOS()) {
      //   this.global.webkit.messageHandlers.myInsightsAPI.postMessage(request);
      // } else {
      //   this.global.parent.postMessage(request, '*');
      // }
      const event = new CustomEvent("myinsights-request-message", {
        detail: request
      });
      this.global.dispatchEvent(event);
    });
  }

  /**
   * Fire off a query
   * @param  {Object} queryObject
   * @return {Promise}
   * @example
   * ds.queryRecord({}).then(() => console.log('did it'));
   */
  queryRecord(...rest) {
    const req = this._constructRequest("queryObject", ...rest);
    return this._query(req);
  }
  /**
   * Run a query
   * @param  {Object} queryConfig
   * @return {Promise}
   */
  runQuery(queryConfig) {
    return this._runQueryHelper({
      config: queryConfig,
      type: typeof queryConfig === "string" ? "apiRequest" : "object"
    });
  }
  /**
   * Run a sales data query
   * @param  {Object} queryObject.object, queryObject.fields, queryObject.where, queryObject.sort, queryObject.limit
   * @return {Promise}
   */
  querySalesData(queryConfig) {
    const req = this._constructRequest("querySalesData", { ...queryConfig });
    return this._query(req);
  }
  /**
   * Outer join two query result sets
   * @param  {Object} q1 The query to which to outerjoin q2
   * @param  {Object} q2 The query to which to outerjoin to q1
   * @param  {String} keyForID The member name of what to consider as the primary key
   * @param  {Object} fieldsLabelsToExclude
   * @param  {String} primaryObjectName The object name assigned to the query desired to be the primary query to which to join the non-primary query.
   * @return {Object} Joined query result
   */
  joinQueries(q1, q2, keyForID, fieldsLabelsToExclude, primaryObjectName) {
    let primaryQuery;
    let secondaryQuery;
    let joinTo;
    let joinFrom = { data: {} };
    const joins = {};
    // get the primary query and secondary query
    if (q1.object.name === primaryObjectName) {
      primaryQuery = q1;
      secondaryQuery = q2;
    } else {
      primaryQuery = q2;
      secondaryQuery = q1;
    }
    // figure out which has the key on which to join the queries
    Object.keys(q1.data[0]).forEach(k => {
      if (k === keyForID) {
        joinTo = q1;
        joinFrom = q2;
      }
    });
    /* for (const k in q1.data[0]) {
      if (k === keyForID) {
        joinTo = q1;
        joinFrom = q2;
        break;
      }
    } */
    if (!joinTo) {
      joinTo = q2;
      joinFrom = q1;
    }
    // Set up a dictonary with references to each result on which to join
    let d;
    for (d = joinFrom.data.length; d--; ) {
      joins[joinFrom.data[d].ID.value] = joinFrom.data[d];
    }
    // rename the fields on the secondary result set to reflect their unique relationship to the original results
    for (d = secondaryQuery.data.length; d--; ) {
      const keys = Object.keys(secondaryQuery.data[d]);
      let key;
      while ((key = keys.pop())) {
        if (key !== "date") {
          const propertyToMove = secondaryQuery.data[d][key];
          secondaryQuery.data[d][
            `${secondaryQuery.object.name}.${key}`
          ] = propertyToMove;
          delete secondaryQuery.data[d][key];
        }
      }
    }
    // merge each record with its respective matching record
    // figure out if we need to append the object name to the keyForID in the case where the joinTo === secondaryQuery
    let newJoinKeyName = keyForID;
    if (joinTo.object.name === secondaryQuery.object.name) {
      newJoinKeyName = `${joinTo.object.name}.${keyForID}`;
    }
    for (d = joinTo.data.length; d--; ) {
      // merge joinTo[d] with joins[joinTo[d][keyForID]]
      joinTo.data[d] = veevaUtil.mergeObjects(
        joinTo.data[d],
        joins[joinTo.data[d][newJoinKeyName].value]
      ); // does the key for ID include the object name or not. it will if joinTo === secondaryQuery;
    }

    // begin label renaming and combining
    const deleteExcludedLabels = labels => {
      const excludedKeys = Object.keys(fieldsLabelsToExclude);
      let excludedKey;
      while ((excludedKey = excludedKeys.pop())) {
        for (let n = labels.length; n--; ) {
          if (labels[n].name === excludedKey) {
            labels.splice(n, 1);
          }
        }
      }
      return labels;
    };

    const primaryLabels = deleteExcludedLabels(primaryQuery.fieldLabels);

    const secondaryLabels = deleteExcludedLabels(secondaryQuery.fieldLabels);
    // rename labels in q2
    for (let l = secondaryLabels.length, label; l--; ) {
      label = secondaryLabels[l];
      label.name = `${secondaryQuery.object.name}.${label.name}`;
    }
    joinTo.fieldLabels = primaryLabels.concat(secondaryLabels);
    joinTo.object = primaryQuery.object;
    joinTo.name = primaryQuery.name;
    return joinTo;
  }
  /**
   * Generates an in-statment fragment to be included in a query.
   * @param  {String[]} ids the ids to include in the generated in-statement
   * @return {string} In-statement to use in a query
   */
  getInStatement(ids) {
    const statementEnd = ")";
    const statementBegin = "(";
    return `${statementBegin}'${ids.join("','")}'${statementEnd}`;
  }

  /**
   * Returns the value of a field for a specific record related to the current object
   * @param {String} object The api name of the object on which the field can be found.
   * @param {String} field The name of the field whose value you want returned.
   * @return {Promise<String>}
   */
  getDataForCurrentObject(object, field) {
    const req = this._constructRequest("getDataForObjectV2", { object, field });
    return this._query(req);
  }
  /**
   * Get the labels for the specified objects.
   * @param  {String[]} objects API names of objects for which to retrieve object labels.
   * @return {Promise}
   */
  getObjectLabels(objects) {
    const req = this._constructRequest("getObjectLabels", { objects });
    return this._query(req);
  }
  /**
   * Get the labels for the fields in a query.
   * @param  {Object} queryConfig
   * @return {Promise}
   */
  getFieldLabels(queryConfig) {
    this._validateQueryConfig(queryConfig);
    const { object, fields } = queryConfig;

    const respHandler = function respHandler(resp) {
      const labels = resp[object];
      return fields.map(field => ({ name: field, display: labels[field] }));
    };
    const req = this._constructRequest("getFieldLabel", { object, fields });
    return this._query(req).then(respHandler);
  }
  /**
   * Returns the translated label for each of the picklist values of the specified field
   * @param  {String} object Object API name
   * @param  {String} field Field API name for which to get the picklist labels
   * @return {Promise}
   */
  getPicklistValueLabels(object, field) {
    const req = this._constructRequest("getPicklistValueLabels", {
      object,
      field
    });
    return this._query(req);
  }
  /**
   * Get veeva messages values for the keys passed in tokens.
   * @param  {String[]} tokens The tokens for which to get translations
   * @param  {String} languageLocaleKey Locale key of the translations to return.
   * @return {Promise}
   */
  getVeevaMessagesWithDefault(tokens, languageLocaleKey) {
    const config = veevaUtil.copyObject(this._queries.translations);
    const whereSubClauses = [];
    let index = tokens.length;
    const cachedResponses = [];
    const cache = this._translationsCache;
    while (index--) {
      if (!cache[`${tokens[index].msgName}:${tokens[index].msgCategory}`]) {
        whereSubClauses.push(
          `(Name='${tokens[index].msgName}' AND Category_vod__c='${tokens[index].msgCategory}')`
        );
      } else {
        cachedResponses.push(
          cache[`${tokens[index].msgName}:${tokens[index].msgCategory}`]
        );
      }
    }
    config.where += `(${whereSubClauses.join(
      " OR "
    )}) AND Language_vod__c='${languageLocaleKey}'`;
    // if all the responses were in the cache, then this will be empty and we just return the cached responses.
    let promise;
    if (whereSubClauses.length) {
      promise = this.queryRecord(config).then(resp => {
        const data = resp.Message_vod__c;
        let d = data.length;
        while (d--) {
          if (
            !cache[`${data[d].Name.value}:${data[d].Category_vod__c.value}`]
          ) {
            cache[`${data[d].Name.value}:${data[d].Category_vod__c.value}`] =
              data[d];
          }
        }
        return data.concat(cachedResponses);
      });
    } else {
      promise = new Promise(resolve => {
        resolve(cachedResponses);
      });
    }
    return promise;
  }
  /**
   * Navigate to a new record interface in the native application.
   * @param  {Object} config
   * @return {Promise}
   */
  newRecord(config) {
    const req = this._constructRequest("newRecord", config);
    return this._query(req);
  }
  /**
   * Navigate to a view record interface in the native application.
   * @param  {Object} config
   * @return {Promise}
   */
  viewRecord(config) {
    const req = this._constructRequest("viewRecord", config);
    return this._query(req);
  }
  /**
   * Navigate to an edit record interface in the native application.
   * @param  {Object} config
   * @return {Promise}
   */
  editRecord(config) {
    const req = this._constructRequest("editRecord", config);
    return this._query(req);
  }
  /**
   * Navigate to a native application interface.
   * @param  {Object} config
   * @return {Promise}
   */
  smartLinking(config) {
    const req = this._constructRequest("smartLinking", config);
    return this._query(req);
  }
  /**
   * Navigate to a suggestion execution interface in the native application.
   * @param  {String} suggestionId
   * @param  {String} actionType
   * @return {Promise}
   */
  executeSuggestionAction(suggestionId, actionType) {
    return this.smartLinking({
      object: "Suggestion_vod__c",
      action: actionType,
      options: {
        record: {
          Id: suggestionId
        }
      }
    });
  }
  /**
   * Navigate to a media presentation interface in the native application.
   * @param  {String} account
   * @param  {String} presentationId
   * @param  {String} keyMessageMediaFileName
   * @return {Promise}
   */
  launchMediaForAccount(account, presentationId, keyMessageMediaFileName) {
    return this.smartLinking({
      object: "Clm_Presentation_vod__c",
      action: "launch",
      options: {
        record: {
          Presentation_Id_vod__c: presentationId,
          Key_Message_vod__c: {
            Media_File_Name_vod__c: keyMessageMediaFileName
          }
        },
        prefill: {
          Account: {
            Id: account
          }
        }
      }
    });
  }
  /**
   * Check for objects available in the current permission set.
   * @return {Promise}
   */
  getAvailableObjects() {
    const req = this._constructRequest("getAvailableObjects");
    return this._query(req);
  }
  /**
   * Retrieve the metadata for the specified object.
   * @param  {Object} queryConfig
   * @return {Promise}
   */
  getObjectMetadata(queryConfig) {
    const req = this._constructRequest("getObjectMetadata", queryConfig.object);
    return this._query(req);
  }
  /**
   * Retrieve the record type labels for the specified object.
   * @param  {String} objectName
   * @return {Promise}
   */
  getRecordTypeLabels(objectName) {
    const req = this._constructRequest("getRecordTypeLabels", {
      object: objectName
    });
    return this._query(req);
  }

  /**
   * CDW Query Record
   * @param  {Object}
   * @return {Promise}
   */
  queryVDSRecord(queryConfig) {
    const req = this._constructRequest("queryVDSRecord", queryConfig);
    return this._query(req);
  }

  /**
   * CRM Data Engine
   */

  _formatDataEngineConfig(config) {
    if (config != null && config.fields) {
      config.fields = this._formatDataConfigFieldArray(config.fields);
    }
    if (
      config != null &&
      config.where != null &&
      config.where.type != null &&
      config.where.type.toUpperCase() !== "ALL_OF" &&
      config.where.type.toUpperCase() !== "ANY_OF"
    ) {
      const whereCopy = Object.assign({}, config.where);
      config.where = queryBuilder.anyOf([whereCopy]);
    }
    return config;
  }

  _formatDataConfigFieldArray(arr) {
    let formattedArr = [];
    for (const val of arr) {
      if (typeof val === "string") {
        formattedArr.push(queryBuilder.field(val));
      } else if (
        typeof val === "object" &&
        val.type &&
        val.type.toUpperCase() === "CONCAT"
      ) {
        val.arguments = this._formatDataConfigFieldArray(val.arguments);
        formattedArr.push(val);
      } else {
        formattedArr.push(val);
      }
    }
    return formattedArr;
  }

  queryDataEngine = function(configObject) {
    configObject = this._formatDataEngineConfig(configObject);
    const req = this._constructRequest("queryDataEngine", configObject);
    return this._query(req);
  };

  getDataEngineTables = function() {
    const req = this._constructRequest("getDataEngineTables");
    return this._query(req);
  };

  getDataEngineTableMetadata = function(tableName) {
    const req = this._constructRequest("getDataEngineTableMetadata", {
      tableName: tableName
    });
    return this._query(req);
  };

  getAlignedTerritories = function(config) {
    const sanitizedBoolean = config ? config.includeChildren === true : false;
    const req = this._constructRequest("getAlignedTerritories", {
      includeChildren: sanitizedBoolean
    });
    return this._query(req);
  };

  getFeedbackMetadata = function() {
    const req = this._constructRequest("getFeedbackMetadata", {});
    return this._query(req);
  };

  getFeedbackData = function() {
    const req = this._constructRequest("getFeedbackData", {});
    return this._query(req);
  };


  getAttendeeDataForCurrentCall = function() {
    const req = this._constructRequest("getAttendeeDataForCurrentCall");
    return this._query(req);
  };
}
export default DataAccessLibrary;
